import { createContext, useContext, ReactNode } from 'react';
import { OutgoingEventProductDto, OutgoingSectionDto } from 'api/dtos/events/products';
import { useDeleteSectionMutation, useCreateSectionMutation } from 'legacy/events/hooks';

interface SectionsContextValue {
  // Data
  eventId: string;
  tiers?: OutgoingEventProductDto[];
  sections?: OutgoingSectionDto[];
  isEventLoading?: boolean;
  tiersBySection: Record<string, OutgoingEventProductDto[]>;
  sortedSections?: OutgoingSectionDto[];
  hasCustomSections: boolean;
  totalTierCount: number;
  defaultTierCount: number;
  sectionStats: Record<string, { tierCount: number; isEmpty: boolean }>;
  
  // Section Management State
  selectedSection?: string;
  sectionNames: Record<string, string>;
  expandedSections: Record<string, boolean>;
  
  // Actions
  editSection: (sectionId: string, name: string) => void;
  handleNameChange: (sectionId: string, name: string) => void;
  toggleSection: (sectionId: string) => void;
  clearSelectedSection: () => void;
  clearSectionNames: () => void;
  setSelectedSection: (sectionId: string | undefined) => void;
  
  // Mutations
  createSectionMutation: ReturnType<typeof useCreateSectionMutation>;
  deleteMutation: ReturnType<typeof useDeleteSectionMutation>;
  updateSectionName: (sectionId: string, name: string) => Promise<void>;
  deleteSection: (sectionId: string) => void;
  
  // Callbacks
  onDeleteSection?: (sectionId: string) => void;
  addEventPricingTierToSection?: (sectionId: string, tier: OutgoingEventProductDto) => void;
  setAddTierToSectionId?: (sectionId: string) => void;
  onReorder?: (tiers: OutgoingEventProductDto[], sections: OutgoingSectionDto[]) => void;
  
  // Children render prop
  children: (tickets: OutgoingEventProductDto[], sectionId: string) => ReactNode;
}

const SectionsContext = createContext<SectionsContextValue | null>(null);

export const useSectionsContext = () => {
  const context = useContext(SectionsContext);
  if (!context) {
    throw new Error('useSectionsContext must be used within a SectionsProvider');
  }
  return context;
};

interface SectionsProviderProps {
  value: SectionsContextValue;
  children: ReactNode;
}

export const SectionsProvider = ({ value, children }: SectionsProviderProps) => {
  return (
    <SectionsContext.Provider value={value}>
      {children}
    </SectionsContext.Provider>
  );
}; 